import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'

import Experience from './Experience.js'
import SphereVertexShader from './shaders/lights/vertex.glsl'
import SphereFragmentShader from './shaders/lights/fragment.glsl'

export default class Renderer {
  constructor () {
    this.experience = new Experience()
    this.canvas = this.experience.canvas

    this.sizes = this.experience.sizes
    this.scene = this.experience.scene
    this.camera = this.experience.camera
    this.debug = this.experience.debug

    this.settings = {
      exposure: 3,
      bloomStrength: 0,
      bloomThreshold: 0.061,
      bloomRadius: 0,
      scene: 'Scene with Glow'
    }

    this.ENTIRE_SCENE = 0
    this.BLOOMSCENE = 1
    this.materials = {}

    this.bloomLayer = new THREE.Layers()
    this.bloomLayer.set(this.BLOOM_SCENE)

    this.setInstanceProcessing()
    // this.setNewRendering()
    this.setGUI()
  }

  setInstance () {
    this.instance = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    })

    this.instance.physicallyCorrectLights = true
    //  this.instance.outputEncoding = THREE.sRGBEncoding
    this.instance.toneMapping = THREE.CineonToneMapping
    this.instance.toneMappingExposure = 1.75
    // this.instance.shadowMap.enabled = true
    // this.instance.shadowMap.type = THREE.PCFSoftShadowMap
    this.instance.setClearColor('#000')
    this.instance.setSize(this.sizes.width, this.sizes.height)
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2))
  }

  setInstanceProcessing () {
    this.instance = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    })
    this.instance.setClearColor('#000')
    this.instance.setSize(this.sizes.width, this.sizes.height)
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2))

    this.renderScene = new RenderPass(this.scene, this.camera.instance)

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(this.sizes.width, this.sizes.height),
      1.5,
      0.4,
      0.85
    )
    this.bloomPass.threshold = this.settings.bloomThreshold
    this.bloomPass.strength = this.settings.bloomStrength
    this.bloomPass.radius = this.settings.bloomRadius

    this.composer = new EffectComposer(this.instance)
    this.composer.addPass(this.renderScene)
    this.composer.addPass(this.bloomPass)
  }

  setNewRendering () {
    this.instance = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true
    })
    this.instance.setClearColor('#000')
    this.instance.setSize(this.sizes.width, this.sizes.height)
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2))

    this.renderScene = new RenderPass(this.scene, this.camera.instance)

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(this.sizes.width, this.sizes.height),
      1.5,
      0.4,
      0.85
    )
    this.bloomPass.threshold = this.settings.bloomThreshold
    this.bloomPass.strength = this.settings.bloomStrength
    this.bloomPass.radius = this.settings.bloomRadius

    this.bloomComposer = new EffectComposer(this.instance)
    this.bloomComposer.renderToScreen = false
    this.bloomComposer.addPass(this.renderScene)
    this.bloomComposer.addPass(this.bloomPass)

    this.finalPass = new ShaderPass(
      new THREE.ShaderMaterial({
        uniforms: {
          baseTexture: { value: null },
          bloomTexture: { value: this.bloomComposer.renderTarget2.texture }
        },
        vertexShader: SphereVertexShader,
        fragmentShader: SphereFragmentShader,
        defines: {}
      }),
      'baseTexture'
    )
    this.finalPass.needsSwap = true

    this.composer = new EffectComposer(this.instance)
    this.composer.addPass(this.renderScene)
    this.composer.addPass(this.finalPass)

    this.render(this.composer)
  }

  render (finalComposer) {
    switch (this.settings.scene) {
      case 'Scene only':
        this.setInstance()
        break
      case 'Glow only':
        this.renderBloom(false)
        break
      case 'Scene with Glow':
      default:
        this.renderBloom(true)
        finalComposer.render()
        break
    }
  }

  renderBloom (mask) {
    if (mask === true) {
      console.log(this.scene, this.scene.children)
      console.log(this.scene.children)
      this.scene.traverse(obj => {
        console.log(obj)

        if (obj instanceof THREE.Mesh && this.bloomLayer.test(obj.layers) === false) {
          console.log(obj)

          this.materials[obj.uuid] = obj.material
          // obj.material = darkMaterial
        }
      })
      this.bloomComposer.render()
      this.scene.traverse((obj) => {
        if (this.materials[obj.uuid]) {
          obj.material = this.materials[obj.uuid]
          delete this.materials[obj.uuid]
        }
      })
    } else {
      this.camera.layers.set(this.BLOOM_SCENE)

      this.bloomComposer.render()
      this.camera.layers.set(this.ENTIRE_SCENE)
    }
  }

  setGUI () {
    this.debugFolder = this.debug.ui.addFolder('Renderer')

    this.debugFolder.add(this.instance, 'physicallyCorrectLights')
    this.debugFolder.add(this.instance, 'toneMapping', {
      No: THREE.NoToneMapping,
      Linear: THREE.LinearToneMapping,
      Reinhard: THREE.ReinhardToneMapping,
      Cineon: THREE.CineonToneMapping,
      ACESFilmic: THREE.ACESFilmicToneMapping
    }).onFinishChange(() => {
      this.instance.toneMapping = Number(this.instance.toneMapping)
      // TODO QUAND ON AURA DES MATERIAUX ! updateMaterials()
    })
    this.debugFolder.add(this.instance, 'toneMappingExposure', 0, 4)

    this.postProcessing = this.debugFolder.addFolder('Post-processing')

    this.postProcessing.add(this.settings, 'exposure', 0.1, 3).onChange(value => {
      this.instance.toneMappingExposure = Math.pow(value, 4.0)
    })

    this.postProcessing.add(this.settings, 'bloomThreshold', 0.0, 1.0).onChange(value => {
      this.bloomPass.threshold = Number(value)
    })

    this.postProcessing.add(this.settings, 'bloomStrength', 0.0, 4.0).onChange(value => {
      this.bloomPass.strength = Number(value)
    })

    this.postProcessing.add(this.settings, 'bloomRadius', 0.0, 3.0).step(0.01).onChange(value => {
      this.bloomPass.radius = Number(value)
    })

    this.postProcessing.add(this.settings, 'scene', {
      SceneWithGlow: 'Scene with Glow',
      GlowOnly: 'Glow only',
      SceneOnly: 'Scene only'
    }).onFinishChange(() => { this.render() })
  }

  resize () {
    this.instance.setSize(this.sizes.width, this.sizes.height)
    this.instance.setPixelRatio(Math.min(this.sizes.pixelRatio, 2))

    this.composer.setSize(this.sizes.width, this.sizes.height)
    this.composer.setPixelRatio(Math.min(this.sizes.pixelRatio, 2))
  }

  update () {
    this.composer.render()
  }
}
